import SoundFont from "soundfont-player";

/**
 * Simple MIDI player for playing single notes
 */
class SimpleMidiPlayer {
  constructor() {
    this.audioContext = null;
    this.instrument = null;
    this.isLoaded = false;
    this.loadingPromise = null;
    // Volume is stored as MIDI velocity (0-127), default is 100
    // Load from localStorage or use default
    this.volume = parseInt(localStorage.getItem('midiVolume')) || 100;
  }

  /**
   * Initialize the audio context and load the instrument
   */
  async initialize() {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = (async () => {
      try {
        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          throw new Error('Web Audio API is not supported in this browser');
        }

        this.audioContext = new AudioContext();

        // Load the acoustic grand piano instrument
        console.log('Loading MIDI instrument...');
        this.instrument = await SoundFont.instrument(
          this.audioContext,
          '/acoustic_grand_piano-mp3.js'
        );

        this.isLoaded = true;
        console.log('✅ MIDI instrument loaded successfully');
      } catch (error) {
        console.error('Failed to initialize MIDI player:', error);
        this.isLoaded = false;
        throw error;
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Get current volume (0-127)
   */
  getVolume() {
    return this.volume;
  }

  /**
   * Set volume (0-127) and save to localStorage
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(127, volume));
    localStorage.setItem('midiVolume', this.volume.toString());
  }

  /**
   * Play a single note for a specified duration
   * @param {string} noteName - The note name (e.g., 'C4', 'D#5')
   * @param {number} duration - Duration in seconds (default: 0.5)
   * @param {number} velocity - Velocity/volume (0-127, uses instance volume if not provided)
   */
  async playNote(noteName, duration = 0.5, velocity = null) {
    // Use instance volume if velocity not provided
    const effectiveVelocity = velocity !== null ? velocity : this.volume;
    if (!this.isLoaded) {
      await this.initialize();
    }

    if (!this.instrument) {
      console.error('MIDI instrument not loaded');
      return;
    }

    try {
      // Resume audio context if it's suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Play the note
      const playbackTime = this.audioContext.currentTime;
      const note = this.instrument.play(
        noteName,
        playbackTime,
        {
          gain: effectiveVelocity / 127,
          duration: duration
        }
      );

      console.log(`🎵 Playing MIDI note: ${noteName} for ${duration}s`);
      
      return note;
    } catch (error) {
      console.error(`Failed to play note ${noteName}:`, error);
    }
  }

  /**
   * Stop all currently playing notes
   */
  stopAll() {
    if (this.instrument && this.instrument.stop) {
      this.instrument.stop();
    }
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stopAll();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.instrument = null;
    this.isLoaded = false;
    this.loadingPromise = null;
  }
}

// Create a singleton instance
const midiPlayer = new SimpleMidiPlayer();

export default midiPlayer;
